import { queryOptions } from "@tanstack/react-query";

import { isApiClientError } from "../api/errors";
import type { AccessScope } from "../entities/access";
import { hasPermission } from "../entities/access/permissions";
import { accessScopeSchema } from "../entities/access/types";
import type { ModuleNavigationItem } from "../entities/modules";
import type { IdentityUser } from "../auth";
import type { ShellServices } from "./services";

export interface ShellBootstrap {
  user: IdentityUser;
  permissions: readonly string[];
  navigation: ModuleNavigationItem[];
  scopes: AccessScope[];
  allOrganizations: boolean;
  currentScope: AccessScope | null;
}

function parseScopes(values: readonly unknown[]): AccessScope[] {
  return values.flatMap((value) => {
    const result = accessScopeSchema.safeParse(value);
    return result.success && result.data.is_active ? [result.data] : [];
  });
}

function visibleNavigation(
  items: readonly ModuleNavigationItem[],
  permissions: readonly string[],
): ModuleNavigationItem[] {
  return items
    .filter(
      (item) =>
        item.permission === undefined ||
        hasPermission({ permissions }, item.permission),
    )
    .toSorted(
      (left, right) =>
        (left.order ?? Number.MAX_SAFE_INTEGER) -
          (right.order ?? Number.MAX_SAFE_INTEGER) ||
        left.label.localeCompare(right.label, "ru"),
    );
}

export async function loadShellBootstrap(
  services: ShellServices,
): Promise<ShellBootstrap> {
  const user = await services.identity.currentUser();
  const permissions = user.permissions;
  const scopes = parseScopes(user.scopes);
  const currentScopeResult = accessScopeSchema.safeParse(user.current_scope);
  const currentScope = currentScopeResult.success
    ? currentScopeResult.data
    : null;
  // В режиме «Все организации» текущей организации нет, но навигация нужна.
  const allOrganizations = user.organization_mode === "all";
  let modules: { data: ModuleNavigationItem[] } = { data: [] };
  if (currentScope !== null || allOrganizations) {
    try {
      modules = await services.modules.navigation();
    } catch (error) {
      // Навигация — вспомогательные данные оболочки. Если её реестр, права или
      // выбранная организация сломаны, оболочка и выход из системы всё равно
      // должны работать: иначе пользователь остаётся с пустым экраном.
      if (!isApiClientError(error) || ![403, 409].includes(error.status)) {
        throw error;
      }
    }
  }

  return {
    user,
    permissions,
    navigation: visibleNavigation(modules.data, permissions),
    scopes,
    currentScope,
    allOrganizations,
  };
}

export const shellBootstrapKey = ["shell", "bootstrap"] as const;

export function shellBootstrapQuery(services: ShellServices) {
  return queryOptions({
    queryKey: shellBootstrapKey,
    queryFn: () => loadShellBootstrap(services),
    retry: false,
    staleTime: 30_000,
  });
}
